import React from 'react';
import { Star } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { googleReviews } from '../data/reviews';
import { SITE } from '../data/site';

const GoogleLogo = () => (
  <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center">
    <svg viewBox="0 0 24 24" className="h-[18px] w-[18px] block" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  </span>
);

const ReviewCard = ({ review, isDark, isEn }) => (
  <article
    className={`w-[300px] shrink-0 rounded-2xl p-5 border ${
      isDark
        ? 'bg-white/10 backdrop-blur-sm border-white/10'
        : 'bg-white border-gray-100 shadow-sm'
    }`}
  >
    <div className="flex items-center justify-between mb-3">
      <div className="flex gap-0.5">
        {Array.from({ length: review.rating }).map((_, j) => (
          <Star key={j} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
        ))}
      </div>
      <GoogleLogo />
    </div>
    <p className={`text-sm leading-relaxed mb-4 ${isDark ? 'text-emerald-50' : 'text-gray-700'}`}>
      &ldquo;{isEn ? review.textEn : review.text}&rdquo;
    </p>
    <div className="flex items-center justify-between">
      <p className={`font-medium text-sm ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>
        {review.name}
      </p>
      <p className={`text-xs ${isDark ? 'text-emerald-200/70' : 'text-gray-400'}`}>
        {new Date(review.date).toLocaleDateString(isEn ? 'en-US' : 'tr-TR', {
          year: 'numeric',
          month: 'short',
        })}
      </p>
    </div>
  </article>
);

const GoogleReviews = ({ limit, variant = 'dark', showTitle = true }) => {
  const { t, isEn } = useLanguage();
  const items = googleReviews.filter((r) => r.rating === 5).slice(0, limit || googleReviews.length);
  const marqueeItems = [...items, ...items];

  const isDark = variant === 'dark';

  return (
    <div>
      {showTitle && (
        <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-8 ${isDark ? 'text-white' : ''}`}>
          <div className="flex items-center gap-3">
            <GoogleLogo />
            <div>
              <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-emerald-800'}`}>
                {t('section.googleReviews')}
              </h2>
              <p className={`text-sm ${isDark ? 'text-emerald-200' : 'text-gray-500'}`}>
                {t('reviews.viaGoogle')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
            ))}
            <span className={`ml-2 text-sm font-medium ${isDark ? 'text-emerald-100' : 'text-gray-600'}`}>5.0</span>
          </div>
        </div>
      )}

      <div className="reviews-marquee -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
        <div className="reviews-marquee-track" aria-label={t('section.googleReviews')}>
          {marqueeItems.map((review, i) => (
            <ReviewCard key={`${review.name}-${i}`} review={review} isDark={isDark} isEn={isEn} />
          ))}
        </div>
      </div>

      <div className="text-center mt-8">
        <a
          href={SITE.googleReviewsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-medium transition-colors ${
            isDark
              ? 'bg-white text-emerald-800 hover:bg-emerald-50'
              : 'bg-emerald-600 text-white hover:bg-emerald-700'
          }`}
        >
          {t('cta.googleReviews')}
        </a>
      </div>
    </div>
  );
};

export default GoogleReviews;
